import type { CreditNote, Movement } from '../model/period';
import { formatDayShort, formatFlow, formatPesos } from './formatters';

const cents = (value: number) => Math.round(value * 100) / 100;

export const creditNoteLabel = (note: CreditNote) => (note.side === 'ingreso' ? 'Devolución a cliente' : 'Nota de crédito');

const SPENT_FOR_REAL = 'Solo contamos lo que de verdad gastaste';

const NO_CHANGE: Record<string, string> = {
	no_deducible: 'Esa compra no contaba como gasto de tu trabajo, así que no cambia tu declaración.',
	definitiva: 'En pago definitivo no restamos el IVA de tus gastos, así que no cambia tu declaración.',
};

export const creditNoteWhy = (note: CreditNote) => {
	if (note.side === 'ingreso') {
		return note.effect === 'ninguno'
			? 'Esa venta no suma a tu ingreso, así que no cambia tu declaración.'
			: 'Solo pagas impuestos por lo que de verdad cobraste, así que tu ingreso del mes baja esa cantidad.';
	}
	switch (note.effect) {
		case 'gasto':
			return note.iva > 0
				? `${SPENT_FOR_REAL}, así que tu gasto deducible baja ${formatPesos(note.deductible)} y restamos ${formatPesos(note.iva)} menos de IVA.`
				: `${SPENT_FOR_REAL}, así que tu gasto deducible baja ${formatPesos(note.deductible)}.`;
		case 'iva':
			return `${SPENT_FOR_REAL}, así que restamos ${formatPesos(note.iva)} menos de IVA.`;
		case 'pendiente':
			return 'Cuenta igual que su factura.';
		case 'ninguno':
			return NO_CHANGE[note.reason ?? ''] ?? 'No cambia tu declaración de este mes.';
		default:
			return undefined;
	}
};

export const creditNoteLead = (amount: number, note: CreditNote, name: string) => {
	const money = formatPesos(Math.abs(amount));
	if (note.side === 'ingreso') {
		return `Le devolviste ${money} a ${name} ${note.adjustsOn ? `de tu venta del ${formatDayShort(note.adjustsOn)}` : 'de una venta anterior'}.`;
	}
	return `${name} te devolvió ${money} ${note.adjustsOn ? `de tu compra del ${formatDayShort(note.adjustsOn)}` : 'de una compra anterior'}.`;
};

export const creditNoteExplanation = (amount: number, note: CreditNote, name: string) =>
	[creditNoteLead(amount, note, name), creditNoteWhy(note)].filter(Boolean).join(' ');

export const isCreditNote = (movement: Movement) => Boolean(movement.creditNote);

export const adjustmentsByTarget = (movements: Movement[]) => {
	const targets = new Set(movements.filter((movement) => !movement.creditNote).map((movement) => movement.id));
	const out = new Map<string, Movement[]>();
	movements.forEach((movement) => {
		const target = movement.creditNote?.adjusts;
		if (!target || !targets.has(target)) return;
		out.set(target, [...(out.get(target) ?? []), movement]);
	});
	return out;
};

export const isNestedCreditNote = (movement: Movement, adjustments: Map<string, Movement[]>) =>
	Boolean(movement.creditNote?.adjusts && adjustments.get(movement.creditNote.adjusts)?.includes(movement));

export const netOf = (amount: number, adjustments: Movement[]) => cents(amount + adjustments.reduce((sum, note) => sum + note.amount, 0));

export const adjustmentSummary = (adjustments: Movement[]) => {
	const back = formatPesos(Math.abs(adjustments.reduce((sum, note) => sum + note.amount, 0)));
	const verb = adjustments[0]?.creditNote?.side === 'ingreso' ? 'Devolviste' : 'Te devolvieron';
	return adjustments.length === 1
		? `${verb} ${back} el ${formatDayShort(adjustments[0].date)}`
		: `${verb} ${back} en ${adjustments.length} notas de crédito`;
};

export const EXCEEDS_TEXT = 'La nota es mayor que la factura. Revísala con tu contador.';

export const adjustmentsFootnote = (adjustments: Movement[], net: number) => {
	if (net < 0) return EXCEEDS_TEXT;
	return adjustments[0]?.creditNote?.side === 'ingreso' ? 'Solo pagas impuestos por lo que de verdad cobraste.' : `${SPENT_FOR_REAL}.`;
};

const alreadyNetted = (count: number, nested: number) => {
	if (nested === 0) return ` ${SPENT_FOR_REAL}.`;
	if (nested === count) return ` ${count === 1 ? 'Ya la restamos de su factura' : 'Ya las restamos de sus facturas'}, porque ${SPENT_FOR_REAL.toLowerCase()}.`;
	return ` ${nested} ya ${nested === 1 ? 'va restada en su factura' : 'van restadas en sus facturas'}. ${SPENT_FOR_REAL}.`;
};

export const notesCaption = (count: number, total: number, nested: number) =>
	`${count} ${count === 1 ? 'nota de crédito' : 'notas de crédito'} por ${formatFlow(total, false)}: devoluciones o descuentos de tus proveedores.${alreadyNetted(count, nested)}`;

export const creditNoteTracking = (movement: Pick<Movement, 'creditNote'>) =>
	movement.creditNote
		? { tipo_movimiento: 'nota_credito', ligada: Boolean(movement.creditNote.adjusts), lado: movement.creditNote.side }
		: {};
